import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { AlertCircle, Download, Lock, RefreshCw } from "lucide-react";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { H3, H4 } from "@/shared/components/typography";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Separator } from "@/shared/components/ui/separator";
import * as React from "react";
import {
  createContext,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { ErrValidationFailed } from "@bitory-gmbh/nidavellir";

export function DocumentSheetContextProvider({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false);

  const value = useMemo(
    () => ({
      open,
      setOpen,
    }),
    [open, setOpen],
  );

  return (
    <DocumentSheetContext.Provider value={value}>
      {children}
    </DocumentSheetContext.Provider>
  );
}

export interface DocumentSheetContextInterface {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const DocumentSheetContext =
  createContext<DocumentSheetContextInterface>({
    open: false,
    setOpen: () => {},
  });

export function useDocumentSheetContext() {
  return useContext(DocumentSheetContext);
}

export const DocumentSheet = Sheet;

export const DocumentSheetTrigger = SheetTrigger;

export function DocumentSheetContent({
  side = "bottom",
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
}) {
  return <SheetContent side={side} {...props} />;
}

export const DocumentSheetHeader = SheetHeader;

export const DocumentSheetTitle = SheetTitle;

type DocumentSheetMainProps = HTMLAttributes<HTMLDivElement>;

export function DocumentSheetMain({
  children,
  className,
  ...props
}: DocumentSheetMainProps) {
  return (
    <div
      className={cn("grid gap-8 px-4 pb-4 md:grid-cols-5", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function DocumentSheetPreview({
  isLoading,
  objectURL = undefined,
  error = undefined,
  fallbackText = t("PDF konnte nicht gerendert werden."),
  className = "",
}: {
  isLoading: boolean;
  objectURL?: string;
  error?: unknown;
  fallbackText?: string;
  className?: string;
}) {
  if (isLoading) {
    return <DocumentPreviewSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} className="md:col-span-3" />;
  }

  return (
    <div className={cn("md:col-span-3", className)}>
      {/*
        iframe and not object was used, since object caused problems in Safari.
        For some reason, scrolling was not possible.
       */}
      <iframe
        title="PDF Preview"
        src={`${objectURL}#toolbar=0`}
        className="h-[80vh] w-full rounded-lg"
      >
        {fallbackText}
      </iframe>
    </div>
  );
}

export function DocumentPreviewSkeleton() {
  return <Skeleton className="h-[80vh] w-full rounded-lg md:col-span-3" />;
}

type DocumentActionsProps = HTMLAttributes<HTMLDivElement>;

export function DocumentSheetActions({
  children,
  className,
  ...props
}: DocumentActionsProps) {
  return (
    <div className={cn("space-y-4 md:col-span-2", className)} {...props}>
      {children}
    </div>
  );
}

interface DocumentActionsHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  documentNumber: ReactNode;
}

export function DocumentSheetActionsHeader({
  title,
  documentNumber,
  children,
  className,
  ...props
}: DocumentActionsHeaderProps) {
  return (
    <div className={cn("", className)} {...props}>
      <H3>{title}</H3>
      <div>{documentNumber}</div>
      {children}
      <Separator className="mt-4" />
    </div>
  );
}

export function DocumentNotReadyAlert({
  error,
  document,
}: {
  error: ErrValidationFailed | null;
  document: string;
}) {
  if (!error) {
    return null;
  }

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{t(`${document} ist nicht vollständig:`)}</AlertTitle>
      <AlertDescription className="mt-2 flex flex-col space-y-2">
        {Object.entries(error.errors).map(([key, value]) => (
          <div key={key}>
            <div className="font-medium">{key}</div>
            <ul className="ml-6 list-disc [&>li]:mt-1">
              {value.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          </div>
        ))}
      </AlertDescription>
    </Alert>
  );
}

type DocumentSheetActionsContentProps = HTMLAttributes<HTMLDivElement>;

export function DocumentSheetActionsContent({
  children,
  className,
  ...props
}: DocumentSheetActionsContentProps) {
  return (
    <div
      className={cn("h-[73vh] space-y-4 overflow-auto", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function DocumentSheetImmutabilityWarning({
  title = t("Hinweis"),
  description = t(
    "Wenn ein Document heruntergeladen wird, wird es festgeschrieben. Es kann dann nicht mehr geändert werden.",
  ),
}: {
  title?: string;
  description?: string;
}) {
  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  );
}

export function DocumentSheetActionsDownload({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1">
      <H4 className="mb-2">{t("Herunterladen")}</H4>
      {children}
    </div>
  );
}

interface DocumentSheetActionsDownloadButtonProps extends HTMLAttributes<HTMLButtonElement> {
  isLoading: boolean;
}

export function DocumentSheetActionsDownloadButton({
  children,
  className,
  isLoading,
  ...props
}: DocumentSheetActionsDownloadButtonProps) {
  return (
    <Button
      className={cn("justify-start", className)}
      variant="outline"
      disabled={isLoading}
      {...props}
    >
      {isLoading ? (
        <Download className="mr-4 h-5 w-5 animate-bounce" />
      ) : (
        <Download className="mr-4 h-5 w-5" />
      )}
      <span>{children}</span>
    </Button>
  );
}

export function DocumentSheetActionsSetImmutableMutation<T>({
  request,
  mutation,
  title = t("Festschreiben"),
  label = t("Festschreiben"),
  description = t(
    "Das Dokument wird festgeschrieben und kann nicht mehr geändert werden.",
  ),
  onError,
}: {
  request: T;
  mutation: () => readonly [
    (request: T) => void,
    {
      isLoading: boolean;
      error?: unknown;
      isSuccess: boolean;
      reset: () => void;
    },
  ];
  label?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  onError: (error: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [mutate, { isLoading, error, isSuccess, reset }] = mutation();

  const doLock = () => {
    if (isLoading) {
      return;
    }

    mutate(request);
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
    }
  }, [isSuccess, reset]);

  useEffect(() => {
    if (error) {
      onError(error);
    }
  }, [error, onError]);

  return (
    <div className="grid gap-1">
      <H4 className="mb-2">Festschreiben</H4>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="justify-start">
            <Lock className="mr-4 h-4 w-4" />
            <span>{label}</span>
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogDescription> {description} </DialogDescription>
          <RTKQueryErrorAlert error={error} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isLoading}>
                {t("Abbrechen")}
              </Button>
            </DialogClose>
            <Button onClick={doLock} disabled={isLoading}>
              {isLoading && <RefreshCw className="mr-2 h-4 w-4" />}
              <span>{t("Festschreiben")}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
